import { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import CircularProgress from '@shared/components/atoms/CircularProgress/CircularProgress';
import {
  MobileApprovalList,
  MobileFilterSheet,
  MobileSelectScreen,
} from '@shared/components/organisms/mobile';

import { getDefaultDateRange } from '@shared/utils/formatDate';

import searchIcon from '@shared/assets/images/search_Icon_mobile.svg';
import searchFilterIcon from '@shared/assets/images/search-filter_Icon.svg';
import closeIcon from '@shared/assets/images/close_icon.svg';
import refreshIcon from '@shared/assets/images/refresh_icon.svg';

import styles from './MobileDrafterContent.module.scss';

/** 문서상태별 정렬 우선순위 (결재중 > 결재대기 > 결재완료 > 반려) */
function getStateSortPriority(item) {
  const code = Number(item.stateCode ?? item.state);
  const stateStr = String(item.state ?? '').trim();
  if (code === 2 || stateStr === '결재중') return 0;
  if (code === 1 || stateStr === '결재대기') return 1;
  if (code === 3 || stateStr === '결재완료') return 2;
  if (code === 4 || stateStr === '반려') return 3;
  return 4;
}

/**
 * 모바일 기안자 페이지 콘텐츠
 * - 탭, 검색 바, 정렬, 리스트 포함
 * - 필터 바텀시트 지원
 */
function MobileDrafterContent({
  activeTab,
  onTabChange,
  submittedSection,
  rejectedSection,
  onItemClick,
  formOptions,
}) {
  const [searchValue, setSearchValue] = useState('');
  // 실제 검색 값
  const [appliedSearchValue, setAppliedSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('latest');

  // 필터 관련 상태
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [selectField, setSelectField] = useState(null);

  // 기본 날짜 범위 (3개월)
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  // 필터 값 상태 (API 파라미터와 매핑)
  const [filterValues, setFilterValues] = useState({
    date1: defaultRange.start,
    date2: defaultRange.end,
    docType: '',
    stateCode: '',
    title: '',
    name: '',
  });

  // 필터 필드 정의
  const filterFields = useMemo(
    function () {
      return [
        {
          key: 'dateRange',
          type: 'dateRange',
          label: '조회기간',
          startKey: 'date1',
          endKey: 'date2',
          startPlaceholder: defaultRange.start,
          endPlaceholder: defaultRange.end,
        },
        {
          key: 'docType',
          type: 'select',
          label: '문서유형',
          options: formOptions || [{ label: '전체', value: '' }],
        },
        {
          key: 'stateCode',
          type: 'select',
          label: '문서상태',
          options: [
            { label: '전체', value: '' },
            { label: '결재대기', value: 1 },
            { label: '결재중', value: 2 },
            { label: '결재완료', value: 3 },
            { label: '반려', value: 4 },
          ],
        },
        {
          key: 'title',
          type: 'input',
          label: '문서제목',
          placeholder: '제목을 입력해 주세요.',
        },
        {
          key: 'name',
          type: 'input',
          label: '기안자',
          placeholder: '홍길동',
        },
      ];
    },
    [formOptions, defaultRange],
  );

  const currentSection = useMemo(() => {
    switch (activeTab) {
      case 'submitted':
        return submittedSection;
      case 'rejected':
        return rejectedSection;
      default:
        return submittedSection;
    }
  }, [activeTab, submittedSection, rejectedSection]);

  const { tableData, isLoading, isFetching, setSearchParams, approvalRefetch } = currentSection;

  // 정렬 적용: 상신함 = 결재중 문서 최신순(고정), 반려함 = 기안일 최신/오래된순 선택
  const sortedData = useMemo(() => {
    if (!tableData) return [];
    let data = [...tableData];

    // 제목 검색어 필터링 (로컬)
    if (appliedSearchValue) {
      data = data.filter((item) =>
        item.title?.toLowerCase().includes(appliedSearchValue.toLowerCase()),
      );
    }

    return data.sort((a, b) => {
      // 상신함: 결재중 우선, 동일 상태는 기안일 최신순
      if (activeTab === 'submitted') {
        const priorityA = getStateSortPriority(a);
        const priorityB = getStateSortPriority(b);
        if (priorityA !== priorityB) return priorityA - priorityB;
        const dateA = a.date || '';
        const dateB = b.date || '';
        return dateB.localeCompare(dateA);
      }
      // 반려함: 기안일 최신순/오래된순
      const dateA = a.date || '';
      const dateB = b.date || '';
      return sortValue === 'latest' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
    });
  }, [tableData, sortValue, appliedSearchValue, activeTab]);

  // 탭 변경 시 정렬 상태 초기화
  useEffect(
    function () {
      setSortValue('latest');
      setSearchValue('');
      setAppliedSearchValue('');
      setFilterValues({
        date1: defaultRange.start,
        date2: defaultRange.end,
        stateCode: '',
        docType: '',
        title: '',
        name: '',
      });
    },
    [activeTab, defaultRange],
  );

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      // 검색 실행 (이미 useMemo에서 처리됨)
      // 엔터가 눌렷을때만 실행되게 변경
      setAppliedSearchValue(searchValue);
    }
  }

  // 필터 버튼 클릭
  function handleFilterClick() {
    setIsFilterOpen(true);
  }

  // 필터 값 변경
  function handleFilterChange(newValues) {
    setFilterValues(newValues);
  }

  // 필터 초기화
  function handleFilterReset() {
    setFilterValues({
      date1: defaultRange.start,
      date2: defaultRange.end,
      stateCode: '',
      docType: '',
      title: '',
      name: '',
    });
  }

  // 셀렉트 필드 클릭 (풀스크린 선택 화면 열기)
  function handleSelectFieldClick(field, currentValue) {
    setSelectField({
      ...field,
      currentValue: currentValue,
    });
    setIsSelectOpen(true);
  }

  // 셀렉트 값 변경
  function handleSelectChange(value) {
    if (selectField) {
      setFilterValues((prev) => ({ ...prev, [selectField.key]: value }));
    }
    setIsSelectOpen(false);
    setSelectField(null);
  }

  // 탭 타이틀
  const tabTitle = activeTab === 'submitted' ? '상신함' : '반려함';

  function applySearchParams(values) {
    const params = {};

    if (values.date1) params.date1 = values.date1;
    if (values.date2) params.date2 = values.date2;

    if (values.docType) params.formCode = Number(values.docType);
    if (values.stateCode) params.stateCode = Number(values.stateCode);
    if (values.team) params.teamCode = Number(values.team);
    if (values.title?.trim()) params.title = values.title.trim();
    if (values.name?.trim()) params.name = values.name.trim();

    setSearchParams(params);
    setIsFilterOpen(false);
  }

  return (
    <div className={styles.container}>
      {/* 탭 */}
      <div className={styles.tabWrapper}>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'submitted' ? styles.tabActive : ''}`}
          onClick={() => onTabChange('submitted')}
        >
          상신함
        </button>
        <button
          type="button"
          className={`${styles.tab} ${activeTab === 'rejected' ? styles.tabActive : ''}`}
          onClick={() => onTabChange('rejected')}
        >
          반려함
        </button>
      </div>

      {/* 리스트 섹션 */}
      <div className={styles.listSection}>
        {/* 검색 필드 */}
        <div className={styles.searchField}>
          <div className={styles.searchIcon}>
            <img src={searchIcon} alt="검색" width={19} height={19} />
          </div>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="제목을 입력해 주세요."
            value={searchValue}
            maxLength={200}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {searchValue && (
            <button
              type="button"
              className={styles.clearButton}
              onClick={() => {
                setSearchValue('');
                setAppliedSearchValue('');
              }}
              aria-label="검색어 초기화"
            >
              <img src={closeIcon} alt="삭제" width={16} height={16} />
            </button>
          )}
          <button
            type="button"
            className={styles.settingButton}
            aria-label="필터"
            // onClick={handleFilterClick}
            onClick={() => setIsFilterOpen(true)}
          >
            <img src={searchFilterIcon} alt="필터" width={19} height={19} />
          </button>
        </div>

        {/* 새로고침 버튼 (기안문서는 정렬 없이 동일 위치에 버튼만) */}
        <div className={styles.refreshRow}>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => approvalRefetch?.()}
            aria-label="새로고침"
          >
            <img src={refreshIcon} alt="" className={styles.refreshIcon} />
          </button>
        </div>

        {/* 리스트 (새로고침 시 F5와 동일한 로딩 UI 표시) */}
        <div className={styles.listWrapper}>
          {isFetching ? (
            <div className={styles.listLoadingContainer}>
              <CircularProgress size={32} className={styles.listLoadingSpinner} />
              <div className={styles.listLoadingMessage}>문서 목록을 불러오는 중입니다...</div>
            </div>
          ) : (
            <MobileApprovalList
              items={sortedData}
              onItemClick={onItemClick}
              emptyMessage={
                activeTab === 'submitted' ? '상신 문서가 없습니다.' : '반려 문서가 없습니다.'
              }
              isLoading={isLoading}
              isFetching={isFetching}
            />
          )}
        </div>
      </div>

      {/* 필터 바텀시트 */}
      <MobileFilterSheet
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        title={activeTab === 'submitted' ? '상신함' : '반려함'}
        filterFields={filterFields}
        filterValues={filterValues}
        onFilterChange={setFilterValues}
        onApply={applySearchParams}
        onReset={handleFilterReset}
        onSelectFieldClick={(field, currentValue) => {
          setSelectField({ ...field, currentValue });
          setIsSelectOpen(true);
        }}
      />

      {/* 셀렉트 풀스크린 */}
      <MobileSelectScreen
        isOpen={isSelectOpen}
        onClose={() => {
          setIsSelectOpen(false);
          setSelectField(null);
        }}
        title={selectField?.label || '선택'}
        options={selectField?.options || []}
        value={selectField?.currentValue}
        onChange={handleSelectChange}
      />
    </div>
  );
}

MobileDrafterContent.propTypes = {
  activeTab: PropTypes.oneOf(['submitted', 'rejected']),
  onTabChange: PropTypes.func,
  submittedSection: PropTypes.object.isRequired,
  rejectedSection: PropTypes.object.isRequired,
  onItemClick: PropTypes.func,
  formOptions: PropTypes.array,
};

MobileDrafterContent.defaultProps = {
  activeTab: 'submitted',
  onTabChange: () => {},
  onItemClick: () => {},
  formOptions: [{ label: '전체', value: '' }],
};

export default MobileDrafterContent;
